"use client";

import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "@/lib/utils";
import { getPillStyle, isStatusValue, useThemeCache } from "@/hooks/use-color-theme";

type SelectThemeContextValue = {
  value?: string;
  fieldSlotIndex: number;
  setFieldSlotIndex: (value: number) => void;
  optionValues: string[];
  setOptionValues: (value: string[]) => void;
};

const SelectThemeContext = React.createContext<SelectThemeContextValue | null>(null);

function collectOptionValues(children: React.ReactNode): string[] {
  const values: string[] = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    const props = child.props as { value?: unknown; children?: React.ReactNode };
    if (typeof props.value === "string") values.push(props.value);
    if (props.children) values.push(...collectOptionValues(props.children));
  });
  return values;
}

const Select = ({ children, value, defaultValue, ...props }: React.ComponentProps<typeof SelectPrimitive.Root>) => {
  const [fieldSlotIndex, setFieldSlotIndex] = React.useState(0);
  const [optionValues, setOptionValues] = React.useState<string[]>([]);
  const selectedValue = value ?? defaultValue;
  const contextValue = React.useMemo<SelectThemeContextValue>(
    () => ({
      value: typeof selectedValue === "string" ? selectedValue : undefined,
      fieldSlotIndex,
      setFieldSlotIndex,
      optionValues,
      setOptionValues,
    }),
    [selectedValue, fieldSlotIndex, optionValues],
  );

  return (
    <SelectThemeContext.Provider value={contextValue}>
      <SelectPrimitive.Root value={value} defaultValue={defaultValue} {...props}>
        {children}
      </SelectPrimitive.Root>
    </SelectThemeContext.Provider>
  );
};

const SelectGroup = SelectPrimitive.Group;

const SelectValue = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Value>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>
>(({ className, style, ...props }, ref) => {
  useThemeCache();
  const theme = React.useContext(SelectThemeContext);
  const value = theme?.value ?? "";
  const hasValue = Boolean(value);
  const optionIndex = Math.max(0, theme?.optionValues.indexOf(value) ?? 0);
  const themed = hasValue ? getPillStyle(value, theme?.fieldSlotIndex ?? 0, optionIndex) : null;
  const pillStyle: React.CSSProperties | undefined = themed
    ? {
        backgroundColor: themed.bg_color,
        color: themed.text_color,
        borderRadius: 9999,
        padding: "2px 10px",
        fontSize: 12,
        fontWeight: 600,
        lineHeight: "18px",
      }
    : undefined;

  return (
    <SelectPrimitive.Value
      ref={ref}
      className={className}
      style={{ ...pillStyle, ...style }}
      {...props}
    />
  );
});
SelectValue.displayName = SelectPrimitive.Value.displayName;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const localRef = React.useRef<React.ElementRef<typeof SelectPrimitive.Trigger> | null>(null);
  const theme = React.useContext(SelectThemeContext);
  const setRefs = (node: React.ElementRef<typeof SelectPrimitive.Trigger> | null) => {
    localRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  };

  React.useLayoutEffect(() => {
    const node = localRef.current;
    if (!node || !theme) return;
    const scope = node.closest("form") ?? node.closest("[role='dialog']") ?? node.closest("main") ?? document.body;
    const triggers = Array.from(scope.querySelectorAll<HTMLElement>("[data-field-slot-select-trigger='true']"))
      .filter((el) => !isStatusValue(el.dataset.selectedValue ?? ""));
    const index = triggers.indexOf(node);
    theme.setFieldSlotIndex(Math.min(3, Math.max(0, index)));
  }, [theme?.value]);

  return (
    <SelectPrimitive.Trigger
      ref={setRefs}
      data-field-slot-select-trigger="true"
      data-selected-value={theme?.value ?? ""}
      className={cn(
        "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => {
  const theme = React.useContext(SelectThemeContext);
  const values = React.useMemo(() => collectOptionValues(children), [children]);

  React.useEffect(() => {
    theme?.setOptionValues(values);
  }, [theme, values]);

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-(--radix-select-content-transform-origin)",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    {...props}
  >
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
